import { AvatarText } from "@amzn/stencil-react-components/avatar";
import { Card } from "@amzn/stencil-react-components/card";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { RoundedAvatar } from "@amzn/it-shop-shared-components";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useCheckoutForm } from "@/pages/checkout/hooks/use-checkout-form";
import { FormField } from "@/common/components/FormField/form-field";
import React from "react";
import { useUserInfo } from "@/providers/user-info-provider";

const STRING_PATH = "checkout.formPanel.managerCard";

export const GetManagerApprovalCard: React.FC = () => {
  const { form, updateField, isDeviceReturnRequired } = useCheckoutForm();
  const amazonianInfo = useUserInfo();

  const manager = amazonianInfo.manager;

  const managerFullName = `${manager?.basicInfo?.firstName ?? ""} ${manager?.basicInfo?.lastName ?? ""}`.trim();
  const managerSubtitle = `L${manager?.jobInfo?.jobLevel ?? ""}, ${manager?.jobInfo?.businessTitle ?? ""}`;

  return (
    <Card data-testid="manager-approval-card" padding="S300" width="100%">
      <Col gridGap="S300" width="100%">
        {/* Title */}
        <Col gridGap="S300">
          <Text fontSize="T400" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.step1`)}
          </Text>
          <Text fontSize="T200">{getString(`${STRING_PATH}.description`)}</Text>
        </Col>

        {/* Manager info */}
        <Col gridGap="S300">
          <Text fontSize="T300" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.yourManager`)}
          </Text>
          {manager?.basicInfo && (
            <RoundedAvatar
              fullName={managerFullName}
              username={manager?.username ?? ""}
            >
              {manager.jobInfo && <AvatarText>{managerSubtitle}</AvatarText>}
            </RoundedAvatar>
          )}
        </Col>

        <Col gridGap="S300" width="85%">
          <FormField
            label={getString(`${STRING_PATH}.businessJustification`)}
            value={form.businessJustification}
            onChange={(value) => updateField("businessJustification", value)}
            placeholder={getString(`${STRING_PATH}.placeholderBJ`)}
            testId="business-justification-input"
            isTextArea
          />

          {isDeviceReturnRequired && (
            <FormField
              label={getString(`${STRING_PATH}.serialNumberForReturnDevice`)}
              value={form.serialNumberForReturnDevice}
              onChange={(value) => updateField("serialNumberForReturnDevice", value)}
              placeholder={getString(`${STRING_PATH}.placeHolderSerialNumbersForReturnDevices`)}
              tooltipText={getString(`${STRING_PATH}.tooltipSerialNumbersForReturnDevices`)}
              testId="return-devices-input"
              isTextArea
            />
          )}

          <FormField
            label={getString(`${STRING_PATH}.costCenter`)}
            value={form.costCenter}
            onChange={(value) => updateField("costCenter", value)}
            placeholder={getString(`${STRING_PATH}.placeholderCostCenter`)}
            chipText={getString(`${STRING_PATH}.askYourManagerCostCenter`)}
            testId="cost-center-input"
          />

          <FormField
            label={getString(`${STRING_PATH}.carCode`)}
            value={form.carCode}
            onChange={(value) => updateField("carCode", value)}
            placeholder={getString(`${STRING_PATH}.placeholderCarCode`)}
            chipText={getString(`${STRING_PATH}.askYourManagerCarCode`)}
            testId="car-code-input"
          />
        </Col>

        <Row gridGap="S200">
          <Checkbox
            data-testid="manager-confirmation-checkbox"
            checked={form.managerConfirmationCheckbox}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateField("managerConfirmationCheckbox", event.target.checked)
            }
          />
          <Label color="neutral90">{getString(`${STRING_PATH}.managerConfirmation`)}</Label>
        </Row>
      </Col>
    </Card>
  );
};
